import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronUp, XCircle, Search, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useIndexSearch from '../../hooks/useIndexSearch';
import { collection, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const FilterOrderModal = ({ isOpen, onClose, columns, onSave }) => {
  const [orderedColumns, setOrderedColumns] = useState([]);
  
  useEffect(() => {
    if (isOpen && columns.length) {
      setOrderedColumns([...columns]);
    }
  }, [isOpen, columns]);
  
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(orderedColumns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setOrderedColumns(items);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Manage Filter Order</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XCircle size={20} />
          </button>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          Drag and drop to reorder the filters. Use the checkbox to show or hide filters.
          These settings will be applied for all users.
        </p>
        
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="filters">
            {(provided) => (
              <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="bg-gray-50 rounded-md mb-4 flex-1 overflow-y-auto"
            >            
                {orderedColumns.map((column, index) => (
                  <Draggable key={column.tableId + '_' + column.columnName} draggableId={column.tableId + '_' + column.columnName} index={index}>
                    {(provided) => (
                      <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="flex items-center p-3 bg-white border border-gray-100 mb-2 rounded-md"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{column.columnDisplayName}</div>
                        <div className="text-xs text-gray-500">{column.tableDisplayName || column.tableName}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="form-checkbox h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary"
                            checked={!column.hidden}
                            onChange={() => {
                              const updatedColumns = [...orderedColumns];
                              const index = updatedColumns.findIndex(c => 
                                c.tableId === column.tableId && c.columnName === column.columnName
                              );
                              if (index !== -1) {
                                updatedColumns[index] = {
                                  ...updatedColumns[index],
                                  hidden: !updatedColumns[index].hidden
                                };
                                setOrderedColumns(updatedColumns);
                              }
                            }}
                          />
                          <span className="ml-1 text-xs text-gray-600">Show</span>
                        </label>
                        <div className="text-gray-500">:::</div>
                      </div>
                    </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(orderedColumns)}
            className="px-4 py-2 bg-primary text-white rounded-md text-sm"
          >
            Save Order
          </button>
        </div>
      </div>
    </div>
  );
};

const SmartFilters = ({ onSelectFilter, queryText = '', className = '' }) => {
  const [indexedColumns, setIndexedColumns] = useState([]);
  const [loadingColumns, setLoadingColumns] = useState(true);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnValues, setColumnValues] = useState([]);
  const [filteredValues, setFilteredValues] = useState([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [columnValueCount, setColumnValueCount] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [initialRenderComplete, setInitialRenderComplete] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const initialFetchRef = useRef(false);
  const previousTablesRef = useRef('');
  const { allowedTables, currentUser, isAdmin, isAIManager, userAccessConfig } = useAuth();

  // State for showing all filters or just the first 3
  const [showAllFilters, setShowAllFilters] = useState(false);
  
  // Initialize the useIndexSearch hook
  const { 
    loading, 
    error, 
    searchValues, 
    fetchIndexedColumns, 
    fetchColumnValues,
    clearCache,
  } = useIndexSearch({
    allowedTables,
    cachePrefix: 'smartFilters',
    maxResults: 50,
    minChars: 1,
    userAccessConfig
  });

  // Function to fetch custom filter order from Firestore
  const fetchFilterOrder = useCallback(async () => {
    try {
      const filterConfigDoc = await getDoc(doc(db, 'appConfiguration', 'filters'));
      if (filterConfigDoc.exists() && filterConfigDoc.data().order) {
        return filterConfigDoc.data().order;
      }
      return null;
    } catch (err) {
      console.error('Error fetching filter order:', err);
      return null;
    }
  }, []);

  // Function to save custom filter order to Firestore
  const saveFilterOrder = useCallback(async (orderedColumns) => {
    try {
      setSavingOrder(true);
      
      // Extract the necessary information to identify columns, including hidden state
      const orderData = orderedColumns.map(column => ({
        tableId: column.tableId,
        columnName: column.columnName,
        hidden: column.hidden || false
      }));
      
      // Check if the document exists
      const filterConfigDoc = await getDoc(doc(db, 'appConfiguration', 'filters'));
      
      if (filterConfigDoc.exists()) {
        // Update existing document
        await updateDoc(doc(db, 'appConfiguration', 'filters'), {
          order: orderData,
          updatedAt: new Date(),
          updatedBy: currentUser?.email || 'unknown'
        });
      } else {
        // Create new document
        await setDoc(doc(db, 'appConfiguration', 'filters'), {
          order: orderData,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: currentUser?.email || 'unknown',
          updatedBy: currentUser?.email || 'unknown'
        });
      }
      
      return true;
    } catch (err) {
      console.error('Error saving filter order:', err);
      return false;
    } finally {
      setSavingOrder(false);
    }
  }, [currentUser]);

  // Handle saving the new order
  const handleSaveOrder = async (orderedColumns) => {
    const success = await saveFilterOrder(orderedColumns);
    if (success) {
      // Update the local state with new order
      setIndexedColumns(orderedColumns);
      setIsModalOpen(false);
    } else {
      // Show error or handle failure
      alert('Failed to save filter order. Please try again.');
    }
  };

  // Memoize allowedTables to prevent unnecessary re-renders
  const allowedTablesKeys = useMemo(() => {
    return allowedTables ? Object.keys(allowedTables).sort().join(',') : '';
  }, [allowedTables]);

// Fetch initial data based on allowedTablesKeys changes
useEffect(() => {
  const loadColumns = async () => {
    setLoadingColumns(true);
    try {
      // If allowedTables changed, clear the cache first
      if (previousTablesRef.current && previousTablesRef.current !== allowedTablesKeys) {
        console.log('Allowed tables changed, clearing cache');
        if (typeof clearCache === 'function') {
          clearCache();
        }
      }
      
      // Update the ref with current tables
      previousTablesRef.current = allowedTablesKeys;
      
      // Fetch columns from the API
      const columns = await fetchIndexedColumns(false); // Force fresh data
      
      // Try to get custom order from Firestore
      const customOrder = await fetchFilterOrder();
      
      if (customOrder && customOrder.length > 0) {
        // Apply custom order from Firestore
        const orderedColumns = [];
        
        // First add columns in the specified order
        customOrder.forEach(orderItem => {
          const foundColumn = columns.find(
            col => col.tableId === orderItem.tableId && col.columnName === orderItem.columnName
          );
          if (foundColumn) {
            // Include the hidden state from the order configuration
            orderedColumns.push({
              ...foundColumn,
              hidden: orderItem.hidden || false
            });
          }
        });

        // Then add any remaining columns that weren't in the custom order
        columns.forEach(column => {
          const isInCustomOrder = orderedColumns.some(
            orderedCol => 
              orderedCol.tableId === column.tableId && 
              orderedCol.columnName === column.columnName
          );
          
          if (!isInCustomOrder) {
            orderedColumns.push({
              ...column,
              hidden: false // Default to visible for columns not in custom order
            });
          }
        });
        
        setIndexedColumns(orderedColumns);
      } else {
        // No custom order, use the default
        setIndexedColumns(columns);
      }
    } catch (err) {
      console.error('Error loading indexed columns:', err);
    } finally {
      setLoadingColumns(false);
      initialFetchRef.current = true;
    }
  };

  // FIXED: Always load columns when allowedTablesKeys changes
  // This ensures we refresh when user permissions change
  if (allowedTablesKeys) {
    loadColumns();
  }
}, [allowedTablesKeys, fetchIndexedColumns, fetchFilterOrder, clearCache]);

  // Mark initial render as complete after first data fetch
  useEffect(() => {
    if (!loadingColumns && !initialRenderComplete) {
      setInitialRenderComplete(true);
    }
  }, [loadingColumns, initialRenderComplete]);

  // Effect for filtering values using the hook
  useEffect(() => {
    const filterColumnValues = async () => {
      if (!selectedColumn) return;
      
      // Check if this column has more than 10k values
      const requiresMinChars = columnValueCount > 10000;
      const searchTermTrimmed = searchTerm.trim();
      const meetsMinChars = searchTermTrimmed.length >= 3;
      
      // Early return with empty values for large datasets with < 3 chars
      if (requiresMinChars && searchTermTrimmed && !meetsMinChars) {
        setFilteredValues([]);
        setHighlightedIndex(-1);
        return;
      }
      
      // For large datasets with empty search, show limited preview or empty list
      if (requiresMinChars && !searchTermTrimmed) {
        // Option 1: Show a limited preview (first 50 items)
        // setFilteredValues(columnValues.slice(0, 50));
        
        // Option 2: Show empty list until search criteria is met
        setFilteredValues([]);
        setHighlightedIndex(-1);
        return;
      }
      
      // Normal search operation for small datasets or searches with 3+ chars
      if (searchTermTrimmed) {
        setLoadingValues(true);
        try {
          const searchColumnId = `${selectedColumn.tableId}_${selectedColumn.columnName}`;
          const { results } = await searchValues(searchTermTrimmed, { 
            searchColumnId, 
            exactMatchOnly: false,
            skipQuotedCheck: true
          });
          setFilteredValues(results);
        } catch (err) {
          console.error('Error filtering values:', err);
          setFilteredValues([]);
        } finally {
          setLoadingValues(false);
        }
      } else {
        // For small datasets, show all values when search is empty
        if (!requiresMinChars) {
          setFilteredValues(columnValues);
        }
      }
      setHighlightedIndex(-1);
    };

    filterColumnValues();
  }, [searchTerm, columnValues, selectedColumn, searchValues, columnValueCount]);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click was outside the dropdown and its trigger button
      if (dropdownRef.current && 
          !dropdownRef.current.contains(event.target) && 
          !event.target.closest('[data-filter-button]')) {
        setSelectedColumn(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (selectedColumn && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [selectedColumn]);

  const handleToggleColumn = useCallback(async (column, event) => {
    // Stop event propagation to prevent any conflicts
    if (event) {
      event.stopPropagation();
    }
    
    if (selectedColumn && selectedColumn.columnName === column.columnName && 
        selectedColumn.tableId === column.tableId) {
      // Close dropdown if clicking the same column
      setSelectedColumn(null);
      setSearchTerm('');
    } else {
      // Open dropdown for a different column
      setSelectedColumn(column);
      setSearchTerm('');
      
      // Fetch column values using the hook
      setLoadingValues(true);
      try {
        const values = await fetchColumnValues(column);
        setColumnValues(values);
        setFilteredValues(values);
        setColumnValueCount(values.length); // Track the number of values
      } catch (err) {
        console.error('Error fetching column values:', err);
        setColumnValues([]);
        setFilteredValues([]);
        setColumnValueCount(0); // Reset count on error
      } finally {
        setLoadingValues(false);
      }
    }
  }, [selectedColumn, fetchColumnValues]);

  const handleSelectValue = useCallback(async (value) => {
    if (!selectedColumn) return;
    
    // Format the filter with proper quoting
    const formattedValue = `"${value}"`;
    
    // Update selected filters state (no change to this part)
    const key = `${selectedColumn.tableId}_${selectedColumn.columnName}`;
    setSelectedFilters(prev => {
      const updatedFilters = { ...prev };
      if (!updatedFilters[key]) {
        updatedFilters[key] = [];
      }
      
      // Only add if not already present
      if (!updatedFilters[key].includes(value)) {
        updatedFilters[key] = [...updatedFilters[key], value];
      }
      
      return updatedFilters;
    });
  
    // Find all occurrences of this value across all tables and columns
    let allValueContexts = [{
      value: value,
      tableName: selectedColumn.tableDisplayName,
      tableId: selectedColumn.tableId,
      columnName: selectedColumn.columnName,
      isExactMatch: true
    }];
  
    try {
      setLoadingValues(true); // Show loading state while searching
      
      // Use searchValues to find all occurrences of this value
      const { results } = await searchValues(value, { 
        exactMatchOnly: true, // Only want exact matches of this value
        skipQuotedCheck: true  // Skip checking for quotes since we want a direct value search
      });
      
      if (results && results.length > 0) {
        // Find the result with an exact match to our value
        const exactMatch = results.find(result => 
          result.value.toLowerCase() === value.toLowerCase()
        );
        
        if (exactMatch && exactMatch.valueContext && Array.isArray(exactMatch.valueContext)) {
          // Use the valueContext from the search results
          allValueContexts = exactMatch.valueContext;
        }
      }
    } catch (err) {
      console.error('Error finding value occurrences:', err);
      // If search fails, we'll fall back to the single context we know about
    } finally {
      setLoadingValues(false);
    }
  
    // Call the callback for QueryInput with ALL occurrences as context
    onSelectFilter({
      suggestion: {
        value: value,
        tableId: selectedColumn.tableId,
        columnName: selectedColumn.columnName,
        tableName: selectedColumn.tableDisplayName,
        valueContext: allValueContexts  // This now contains ALL occurrences of the value
      },
      replacementText: formattedValue
    });
  
    // Reset dropdown state
    setSelectedColumn(null);
    setSearchTerm('');
  }, [selectedColumn, onSelectFilter, searchValues, setLoadingValues]);

  const handleRemoveFilter = useCallback((tableId, columnName, value) => {
    const key = `${tableId}_${columnName}`;
    
    // Update selected filters state
    setSelectedFilters(prev => {
      const updatedFilters = { ...prev };
      if (updatedFilters[key]) {
        updatedFilters[key] = updatedFilters[key].filter(v => v !== value);
        
        // Remove the key if no values left
        if (updatedFilters[key].length === 0) {
          delete updatedFilters[key];
        }
      }
      return updatedFilters;
    });
    
    // Notify parent component to remove from input
    // Create the same formatted text that was added to the input
    const formattedValue = `"${value}"`;
    
    // Call the callback with special action for removal
    onSelectFilter({
      action: 'remove',
      value: value,
      formattedValue: formattedValue,
      suggestion: {
        tableId: tableId,
        columnName: columnName
      }
    });
  }, [onSelectFilter]);

  const handleKeyDown = useCallback((e) => {
    if (!selectedColumn) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredValues.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : 0
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredValues.length) {
          handleSelectValue(filteredValues[highlightedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setSelectedColumn(null);
        setSearchTerm('');
        break;
      default:
        break;
    }
  }, [selectedColumn, filteredValues, highlightedIndex, handleSelectValue]);

  // Count total filters applied
  const totalFiltersCount = Object.values(selectedFilters).reduce(
    (sum, values) => sum + values.length, 0
  );

  // Create an array for all the filters to be displayed
  const filterChips = useMemo(() => {
    const chips = [];
    
    if (totalFiltersCount > 0) {
      Object.entries(selectedFilters).forEach(([key, values]) => {
        // Format should be tableId_columnName, but columnName might contain underscores
        // Instead of parsing, search through all columns to find a match
        const matchingColumn = indexedColumns.find(column => {
          const columnKey = `${column.tableId}_${column.columnName}`;
          return columnKey === key;
        });
        
        if (matchingColumn && values.length > 0) {
          values.forEach(value => {
            chips.push({
              id: `${key}_${value}`,
              tableId: matchingColumn.tableId,
              columnName: matchingColumn.columnName,
              columnDisplayName: matchingColumn.columnDisplayName,
              value
            });
          });
        }
      });
    }
    
    return chips;
  }, [selectedFilters, indexedColumns, totalFiltersCount]);

  useEffect(() => {
    if (filterChips.length === 0) return;
  
    // If the input box is empty, remove all chips
    if (!queryText) {
      filterChips.forEach(chip => {
        handleRemoveFilter(chip.tableId, chip.columnName, chip.value);
      });
      return;
    }
  
    // Otherwise, remove only chips whose quoted value is no longer present
    filterChips.forEach(chip => {
      const formatted = `"${chip.value}"`;
      if (!queryText.includes(formatted)) {
        handleRemoveFilter(chip.tableId, chip.columnName, chip.value);
      }
    });
  }, [queryText, filterChips, handleRemoveFilter]);
  


  // Short-circuit return if nothing to render yet
  if (!initialRenderComplete && loadingColumns) {
    return (
      <div className={`mb-4 ${className} animate-pulse`}>
        <div className="flex flex-wrap items-center gap-2 justify-center">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-9 w-24 bg-gray-200 rounded-md"></div>
          ))}
        </div>
      </div>
    );
  }

  // Return null when no columns exist and we've completed the initial loading
  if (initialRenderComplete && indexedColumns.length === 0) {
    return null;
  }

  return (
    <div className={`mb-4 ${className}`}>
      {/* Column Filter Buttons */}
      <div className="mb-1 flex flex-wrap items-center gap-2 justify-center relative">
        {(isAdmin || isAIManager) && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="mr-1 p-1.5 rounded-full text-gray-500 hover:text-primary hover:bg-gray-100 transition-colors"
            title="Manage filter order"
          >
            <Settings size={16} />
          </button>
        )}
        <span className="text-gray-500 text-sm mr-1">Filter by</span>
        {(showAllFilters ? indexedColumns : indexedColumns.slice(0, 4))
          .filter(column => !column.hidden) // Filter out hidden columns
          .map(column => {
          const key = `${column.tableId}_${column.columnName}`;
          const hasFilters = selectedFilters[key] && selectedFilters[key].length > 0;
          
          return (
            <div key={key} className="relative group">
              <span
                className="absolute left-1/2 -translate-x-1/2 -top-7 
                          bg-gray-700 text-white text-xs rounded py-0.5 px-2
                          opacity-0 group-hover:opacity-100 transition-opacity
                          whitespace-nowrap pointer-events-none z-50"
              >
                {column.tableDisplayName || column.tableName}
              </span>
              
              <button
                data-filter-button="true"
                className={`px-4 py-2 rounded-full text-sm flex items-center gap-1.5 transition-all cursor-pointer
                  ${hasFilters 
                    ? 'bg-white border border-secondary/30 text-primary hover:bg-gray-50' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                  }
                  ${selectedColumn && selectedColumn.columnName === column.columnName && selectedColumn.tableId === column.tableId 
                    ? 'ring-1 ring-secondary/40' 
                    : ''
                  }
                `}
                onClick={(e) => handleToggleColumn(column, e)}
                aria-expanded={selectedColumn && selectedColumn.columnName === column.columnName && selectedColumn.tableId === column.tableId}
                aria-haspopup="true"
              >
                <span>{column.columnDisplayName}</span>
                {hasFilters && (
                  <span className="bg-secondary/10 text-primary text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center">
                    {selectedFilters[key].length}
                  </span>
                )}
                {selectedColumn && selectedColumn.columnName === column.columnName && selectedColumn.tableId === column.tableId 
                  ? <ChevronUp size={16} className="text-secondary" /> 
                  : <ChevronDown size={16} />
                }
              </button>

              {/* Dropdown for column values */}
              {selectedColumn && selectedColumn.columnName === column.columnName && 
               selectedColumn.tableId === column.tableId && (
                <div 
                  ref={dropdownRef}
                  className="absolute mt-1 bg-white border border-gray-200 rounded-md z-50 w-64"
                >
                  <div className="p-2">
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="w-full bg-gray-50 text-primary rounded-md pl-8 pr-4 py-2 focus:ring-2 focus:ring-secondary/30 focus:outline-none text-sm border border-gray-200"
                        placeholder={`Search ${column.columnDisplayName}...`}
                      />
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-primary/40" />
                    </div>
                  </div>
                  
                  <div className="max-h-60 overflow-y-auto">
                    {loadingValues ? (
                      <div className="px-4 py-2 text-primary/60 text-sm">Loading values...</div>
                    ) : columnValueCount > 10000 ? (
                      searchTerm.trim().length === 0 ? (
                        <div className="px-4 py-2 text-gray-500 text-sm">
                          Large dataset ({columnValueCount.toLocaleString()} values). Type at least 3 characters to search.
                        </div>
                      ) : searchTerm.trim().length < 3 ? (
                        <div className="px-4 py-2 text-gray-500 text-sm">
                          Type {3 - searchTerm.trim().length} more character{searchTerm.trim().length === 2 ? '' : 's'} to search
                        </div>
                      ) : filteredValues.length > 0 ? (
                        <ul className="py-1">
                          {filteredValues.map((item, index) => {
                            const isSelected = selectedFilters[key] && 
                                            selectedFilters[key].includes(item.value);
                            
                            return (
                              <li
                                key={index}
                                className={`px-4 py-2 cursor-pointer text-sm ${
                                  index === highlightedIndex ? 'bg-secondary/10' : 'hover:bg-gray-50'
                                } ${isSelected ? 'text-primary font-medium' : 'text-gray-700'}`}
                                onClick={() => handleSelectValue(item.value)}
                              >
                                {item.value}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div className="px-4 py-2 text-primary/60 text-sm">No matching values found</div>
                      )
                    ) : filteredValues.length > 0 ? (
                      <ul className="py-1">
                        {filteredValues.map((item, index) => {
                          const isSelected = selectedFilters[key] && 
                                          selectedFilters[key].includes(item.value);
                          
                          return (
                            <li
                              key={index}
                              className={`px-4 py-2 cursor-pointer text-sm ${
                                index === highlightedIndex ? 'bg-secondary/10' : 'hover:bg-gray-50'
                              } ${isSelected ? 'text-primary font-medium' : 'text-gray-700'}`}
                              onClick={() => handleSelectValue(item.value)}
                            >
                              {item.value}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <div className="px-4 py-2 text-primary/60 text-sm">No values found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        {/* More button */}
        {indexedColumns.length > 4 && (
          <button
            onClick={() => setShowAllFilters(!showAllFilters)}
            className="px-4 py-2 rounded-full text-sm transition-all duration-200
                      bg-transparent hover:bg-gray-100
                      text-gray-500 hover:text-gray-700
                      flex items-center border border-transparent hover:border-gray-200"
          >
            {showAllFilters ? (
              <>
                Show less <ChevronUp className="ml-1 w-4 h-4" />
              </>
            ) : (
              <>
                More <ChevronDown className="ml-1 w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>

      {/* Display selected filters separately */}
      {filterChips.length > 0 && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-2 justify-center">
            {filterChips.map(chip => (
              <div 
                key={chip.id}
                className="bg-secondary/10 border border-secondary/30 text-primary text-xs rounded-full px-3 py-1.5 flex items-center gap-1"
              >
                <span className="font-medium text-gray-500">{chip.columnDisplayName}:</span>
                <span className="font-medium">{chip.value}</span>
                <button
                  className="ml-1 text-primary/80 hover:text-primary focus:outline-none transition-colors"
                  onClick={() => handleRemoveFilter(chip.tableId, chip.columnName, chip.value)}
                >
                  <XCircle size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Filter Order Modal */}
      <FilterOrderModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        columns={indexedColumns}
        onSave={handleSaveOrder}
      />
    </div>
  );
};

export default SmartFilters;